'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { Pause, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { landingVideoUrl } from './landing-context';

const motionQuery = '(prefers-reduced-motion: reduce)';

function subscribeToMotion(onChange: () => void) {
  const media = window.matchMedia(motionQuery);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function prefersReducedMotion() {
  return window.matchMedia(motionQuery).matches;
}

function serverReducedMotion() {
  return true;
}

export function HeroVideo({ videoKey, poster, focalPoint, active = true, playbackPaused, onPlaybackPauseChange }: {
  videoKey: string;
  poster?: string;
  focalPoint?: string;
  active?: boolean;
  playbackPaused?: boolean;
  onPlaybackPauseChange?: (paused: boolean) => void;
}) {
  const { t } = useTranslation('marketing');
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = useSyncExternalStore(subscribeToMotion, prefersReducedMotion, serverReducedMotion);
  const [state, setState] = useState({ paused: false, playing: false, failed: false });
  const source = landingVideoUrl(videoKey);
  const enabled = Boolean(source) && active && !reducedMotion && !state.failed;
  const paused = playbackPaused ?? state.paused;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled || paused) return;
    let cancelled = false;
    video.play().catch(() => {
      if (!cancelled) {
        setState((current) => ({ ...current, paused: true, playing: false }));
        onPlaybackPauseChange?.(true);
      }
    });
    return () => {
      cancelled = true;
      video.pause();
    };
  }, [enabled, paused, onPlaybackPauseChange]);

  if (!enabled) return null;

  return (
    <>
      <video
        ref={videoRef}
        src={source}
        poster={poster || undefined}
        autoPlay={!paused}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: focalPoint ?? 'center', opacity: state.playing ? 1 : 0 }}
        onPlaying={() => setState((current) => ({ ...current, playing: true }))}
        onPause={() => setState((current) => ({ ...current, playing: false }))}
        onError={() => setState((current) => ({ ...current, failed: true, playing: false }))}
      />
      <button
        type="button"
        data-test="landing-video-playback"
        className="absolute end-4 bottom-16 z-20 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-black/70 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        aria-label={t(paused ? 'landingVideo.play' : 'landingVideo.pause')}
        onClick={() => {
          if (onPlaybackPauseChange) onPlaybackPauseChange(!paused);
          else setState((current) => ({ ...current, paused: !current.paused }));
        }}
      >
        {paused ? <Play size={18} aria-hidden /> : <Pause size={18} aria-hidden />}
      </button>
    </>
  );
}
